import * as React from 'react'
import { Transition } from 'react-transition-group'
import {
  getTransitionProps,
  reflow,
  getAutoHeightDuration,
  TransitionProps,
  createTransition,
} from '../transitions'
import { useForkRef } from '../../hooks'

export interface GrowProps extends Omit<TransitionProps, 'timeout'> {
  /**
   * Perform the enter transition when it first mounts if `in` is also `true`.
   * Set this to `false` to disable this behavior.
   * @default true
   */
  appear?: boolean
  /**
   * A single child content element.
   */
  children: React.ReactElement<any, any>
  /**
   * The transition timing function.
   * You may specify a single easing or a object containing enter and exit values.
   */
  easing?: TransitionProps['easing']
  /**
   * If `true`, the component will transition in.
   */
  in?: boolean

  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   *
   * Set to 'auto' to automatically calculate transition time based on height.
   * @default 'auto'
   */
  timeout?: TransitionProps['timeout'] | 'auto'
}

function getScale(value: number) {
  return `scale(${value}, ${value ** 2})`
}

const styles = {
  entering: {
    opacity: 1,
    transform: getScale(1),
  },
  entered: {
    opacity: 1,
    transform: 'none',
  },
}

/**
 * The Grow transition is used by the [Tooltip](/material-ui/react-tooltip/) and
 * [Popover](/material-ui/react-popover/) components.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
export const Grow = React.forwardRef<HTMLDivElement, GrowProps>(
  function Grow(props, ref) {
    const {
      addEndListener,
      appear = true,
      children,
      easing,
      in: inProp,
      onEnter,
      onEntered,
      onEntering,
      onExit,
      onExited,
      onExiting,
      style,
      timeout = 'auto',
      ...other
    } = props
    const timer = React.useRef<NodeJS.Timeout>()
    const autoTimeout = React.useRef<number>()

    const nodeRef = React.useRef(null)
    // @ts-ignore
    const handleRef = useForkRef(nodeRef, children.ref, ref)

    const normalizedTransitionCallback =
      (callback: (node: HTMLElement, isAppearing?: boolean) => void) =>
      (maybeIsAppearing: boolean) => {
        if (callback) {
          const node = nodeRef.current!
          // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
          if (maybeIsAppearing === undefined) {
            callback(node)
          } else {
            callback(node, maybeIsAppearing)
          }
        }
      }

    const handleEntering = normalizedTransitionCallback(onEntering as any)

    const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
      reflow(node) // So the animation always start from the start.

      const {
        duration: transitionDuration,
        delay,
        easing: transitionTimingFunction,
      } = getTransitionProps(
        // @ts-ignore
        { style, timeout, easing },
        {
          mode: 'enter',
        }
      )

      let duration
      if (timeout === 'auto') {
        duration = getAutoHeightDuration(node.clientHeight)
        autoTimeout.current = duration
      } else {
        duration = transitionDuration
      }

      node.style.transition = [
        createTransition('opacity', {
          duration,
          delay,
        }),
        createTransition('transform', {
          duration: Number(duration) * 0.666,
          delay,
          easing: transitionTimingFunction,
        }),
      ].join(',')

      if (onEnter) {
        onEnter(node, isAppearing!)
      }
    })

    const handleEntered = normalizedTransitionCallback(onEntered as any)

    const handleExiting = normalizedTransitionCallback(onExiting as any)

    const handleExit = normalizedTransitionCallback(node => {
      const {
        duration: transitionDuration,
        delay,
        easing: transitionTimingFunction,
      } = getTransitionProps(
        // @ts-ignore
        { style, timeout, easing },
        {
          mode: 'exit',
        }
      )

      let duration
      if (timeout === 'auto') {
        duration = getAutoHeightDuration(node.clientHeight)
        autoTimeout.current = duration
      } else {
        duration = transitionDuration
      }

      node.style.transition = [
        createTransition('opacity', {
          duration,
          delay,
        }),
        createTransition('transform', {
          duration: Number(duration) * 0.666,
          // @ts-ignore
          delay: delay || Number(duration) * 0.333,
          easing: transitionTimingFunction,
        }),
      ].join(',')

      node.style.opacity = '0'
      node.style.transform = getScale(0.75)

      if (onExit) {
        onExit(node)
      }
    })

    const handleExited = normalizedTransitionCallback(onExited as any)

    const handleAddEndListener = (next: any) => {
      if (timeout === 'auto') {
        timer.current = setTimeout(next, autoTimeout.current || 0)
      }
      if (addEndListener) {
        // Old call signature before `react-transition-group` implemented `nodeRef`
        addEndListener(nodeRef.current!, next)
      }
    }

    React.useEffect(() => {
      return () => {
        // @ts-ignore
        clearTimeout(timer.current)
      }
    }, [])

    return (
      // @ts-ignore
      <Transition
        appear={appear}
        in={inProp}
        nodeRef={nodeRef}
        onEnter={handleEnter}
        onEntered={handleEntered}
        onEntering={handleEntering}
        onExit={handleExit}
        onExited={handleExited}
        onExiting={handleExiting}
        addEndListener={handleAddEndListener}
        timeout={timeout === 'auto' ? null : timeout}
        {...other}
      >
        {(state: any, childProps: any) => {
          return React.cloneElement(children, {
            style: {
              opacity: 0,
              transform: getScale(0.75),
              visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
              // @ts-ignore
              ...styles[state],
              ...style,
              ...children.props.style,
            },
            ref: handleRef,
            ...childProps,
          })
        }}
      </Transition>
    )
  }
)
// @ts-ignore
Grow.factorSupportAuto = true
